import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import i18n from "i18next";
import { LuMic, LuSquare, LuPlay, LuPause, LuRotateCcw, LuUpload } from "react-icons/lu";

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const RecordSoundTab = forwardRef(
  ({ onAddSound, onServerModal, uploadFunction, onStateChange }, ref) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingBlob, setRecordingBlob] = useState(null);
    const [recordingAudioUrl, setRecordingAudioUrl] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [micError, setMicError] = useState("");
    const [isUploading, setIsUploading] = useState(false);

    const [startTime, setStartTime] = useState(0);
    const [volume, setVolume] = useState(100);

    const [audioName, setAudioName] = useState("");

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);

    const recorderRef = useRef(null);
    const intervalRef = useRef(null);
    const audioRef = useRef(null);

    useEffect(() => {
      return () => {
        clearInterval(intervalRef.current);
        if (recordingAudioUrl) URL.revokeObjectURL(recordingAudioUrl);
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
      };
    }, []);

    const startRecording = useCallback(async () => {
      setMicError("");
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        recorderRef.current = recorder;

        const chunks = [];
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(chunks, { type: "audio/webm" });
          const url = URL.createObjectURL(blob);
          setRecordingBlob(blob);
          setRecordingAudioUrl(url);
          clearInterval(intervalRef.current);
        };

        recorder.start();
        setIsRecording(true);
        setElapsedTime(0);
        setRecordingBlob(null);
        setRecordingAudioUrl(null);
        setIsPlaying(false);
        setCurrentTime(0);
        setProgress(0);
        setDuration(0);

        intervalRef.current = setInterval(() => {
          setElapsedTime((prev) => prev + 1);
        }, 1000);
      } catch (err) {
        setMicError(
          i18n.t("MicrophoneErrorMessage") ||
            "Mikrofona erişilemiyor. Lütfen izin verdiğinizden emin olun."
        );
      }
    }, []);

    const stopRecording = useCallback(() => {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      clearInterval(intervalRef.current);
      setIsRecording(false);
    }, []);

    const resetRecording = useCallback(() => {
      if (isRecording) stopRecording();
      if (recordingAudioUrl) URL.revokeObjectURL(recordingAudioUrl);
      setRecordingBlob(null);
      setRecordingAudioUrl(null);
      setElapsedTime(0);
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
      setDuration(0);
      setMicError("");
      setStartTime(0);
      setVolume(100);
    }, [isRecording, stopRecording, recordingAudioUrl]);

    const handlePlayPause = useCallback(() => {
      if (!audioRef.current) return;
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }, [isPlaying]);

    const handleTimeUpdate = useCallback(() => {
      if (!audioRef.current) return;
      const cur = audioRef.current.currentTime;
      const dur = audioRef.current.duration || 0;
      setCurrentTime(cur);
      setProgress(dur > 0 ? (cur / dur) * 100 : 0);
    }, []);

    const handleLoadedMetadata = useCallback(() => {
      if (!audioRef.current) return;
      setDuration(audioRef.current.duration || 0);
    }, []);

    const handleAudioEnded = useCallback(() => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
    }, []);

    const handleProgressBarClick = useCallback((e) => {
      if (!audioRef.current) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const pct = (e.clientX - rect.left) / rect.width;
      const newTime = pct * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      setProgress(pct * 100);
    }, []);

    const uploadRecording = useCallback(() => {
      if (!recordingBlob) return;
      if (!uploadFunction) {
        console.error("[RecordSoundTab] uploadFunction prop is missing");
        return;
      }

      const name = audioName.trim() || `recording-${Date.now()}`;
      const file = recordingBlob;
      file.name = name + ".mp3";
      file.lastModifiedDate = new Date();

      setIsUploading(true);
      onServerModal?.("", i18n.t("SoundLoading") || "Ses yükleniyor...", true);

      uploadFunction(file, null, (url) => {
        setIsUploading(false);
        onServerModal?.("", i18n.t("SoundLoaded") || "Ses yüklendi", true);

        onAddSound?.({
          file: url,
          name: name,
          startTime: Number(startTime) || 0,
          endTime: (Number(startTime) || 0) + duration,
          volumeLevel: volume / 100,
        });
      });
    }, [
      recordingBlob,
      audioName,
      duration,
      onServerModal,
      uploadFunction,
      onAddSound,
      startTime,
      volume,
    ]);

    useEffect(() => {
      onStateChange?.(!!recordingBlob);
    }, [recordingBlob, onStateChange]);

    useImperativeHandle(
      ref,
      () => ({
        handleSave: () => {
          uploadRecording();
        },
      }),
      [uploadRecording]
    );

    return (
      <div
        className="add-sound-modal__record"
        style={{ display: "flex", flexDirection: "column", gap: "20px" }}
      >
        {/* Audio name input */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {i18n.t("AudioName") || "Ses Adı"}
          </label>
          <input
            type="text"
            value={audioName}
            onChange={(e) => setAudioName(e.target.value)}
            placeholder={i18n.t("EnterAudioName") || "Ses adını girin (isteğe bağlı)"}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: "10px",
              padding: "10px 14px",
              fontSize: "14px",
              color: "#1f2937",
              outline: "none",
            }}
          />
        </div>

        {/* Recording controls */}
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "16px",
            padding: "20px",
            background: "#f8fafc",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            alignItems: "center",
          }}
        >
          {/* Timer / status */}
          <div
            style={{
              fontSize: "32px",
              fontWeight: 700,
              color: isRecording ? "#ef4444" : "#1e293b",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatTime(isRecording ? elapsedTime : duration || elapsedTime)}
          </div>

          {isRecording && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  display: "inline-block",
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: "#ef4444",
                  animation: "pulse 1s infinite",
                }}
              />
              <span style={{ fontSize: "13px", color: "#ef4444", fontWeight: 600 }}>
                {i18n.t("Recording") || "Kaydediliyor..."}
              </span>
            </div>
          )}

          {/* Buttons row */}
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {!isRecording && !recordingBlob && (
              <button
                type="button"
                onClick={startRecording}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  borderRadius: "12px",
                  border: "none",
                  background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(109, 40, 217, 0.3)",
                }}
              >
                <LuMic size={18} />
                {i18n.t("StartRecording") || "Kayıt Başlat"}
              </button>
            )}

            {isRecording && (
              <button
                type="button"
                onClick={stopRecording}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  borderRadius: "12px",
                  border: "none",
                  background: "#ef4444",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(239, 68, 68, 0.3)",
                }}
              >
                <LuSquare size={18} />
                {i18n.t("StopRecording") || "Durdur"}
              </button>
            )}

            {recordingBlob && !isRecording && (
              <button
                type="button"
                onClick={resetRecording}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 18px",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  color: "#64748b",
                  fontWeight: 600,
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                <LuRotateCcw size={15} />
                {i18n.t("ResetRecording") || "Yeniden Kaydet"}
              </button>
            )}
          </div>

          {micError && (
            <div style={{ color: "#ef4444", fontSize: "13px", textAlign: "center" }}>
              {micError}
            </div>
          )}
        </div>

        {/* Playback section */}
        {recordingBlob && recordingAudioUrl && !isRecording && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <audio
              ref={audioRef}
              src={recordingAudioUrl}
              preload="metadata"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleAudioEnded}
              style={{ display: "none" }}
            />

            <div className="add-sound-modal__ai-voice-player">
              <button
                type="button"
                className="add-sound-modal__ai-voice-play"
                onClick={handlePlayPause}
              >
                {isPlaying ? <LuPause /> : <LuPlay />}
              </button>
              <div
                className="add-sound-modal__ai-voice-bar"
                onClick={handleProgressBarClick}
                style={{ cursor: "pointer" }}
              >
                <span
                  className="add-sound-modal__ai-voice-bar-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="add-sound-modal__ai-voice-time">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
          </div>
        )}

        {/* Sound Settings section */}
        {recordingBlob && recordingAudioUrl && !isRecording && (
          <div className="add-sound-modal__upload-settings" style={{ marginTop: "8px" }}>
            <div className="add-sound-modal__upload-settings-title">
              {i18n.t("AddSoundUpload.SoundSettings") || "Sound Settings"}
            </div>
            <div className="add-sound-modal__upload-settings-grid">
              <div className="add-sound-modal__upload-settings-field">
                <label>{i18n.t("AddSoundUpload.StartTime") || "Start Time"}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={startTime}
                  onChange={(event) => setStartTime(event.target.value)}
                  onBlur={(e) => {
                    let val = Number(e.target.value) || 0;
                    if (val < 0) val = 0;
                    setStartTime(val);
                  }}
                />
              </div>
              <div className="add-sound-modal__upload-settings-field">
                <div className="add-sound-modal__upload-settings-row">
                  <label>{i18n.t("AddSoundUpload.Volume") || "Volume"}</label>
                  <span>{volume}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={(event) => setVolume(Number(event.target.value))}
                  className="add-sound-modal__upload-settings-range"
                  style={{
                    background: `linear-gradient(90deg, #6d5dfc ${volume}%, #e2e8f0 ${volume}%)`,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {isUploading && (
          <div style={{ textAlign: "center", color: "#7c3aed", fontSize: "13px", fontWeight: 600 }}>
            {i18n.t("Uploading") || "Yükleniyor..."}
          </div>
        )}
      </div>
    );
  }
);

export default RecordSoundTab;
